import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { CALENDAR_TRIAL_ROUTE } from "../../../../app/router/routes";
import { useGlobalMessageActions } from "../../../../entities/globalMessage";
import { useMyActions } from "../../../../entities/my";
import { IGetTrialDayCalendarResponse, ITrialCalendarFilter, ITrialLesson,
    trialLessonService, TTrialLessonAction, TTrialLessonStatus } from "../../../../entities/trialLesson";
import { TrialLessonCard } from "../../../../features/trialLessonCard";
import { TrialLessonCreate } from "../../../../features/trialLessonCreate/ui/TrialLessonCreate";
import { TrialLessonCreateStudent } from "../../../../features/trialLessonCreateStudent";
import { TrialLessonEdit } from "../../../../features/trialLessonEdit";
import { TrialLessonReschedule } from "../../../../features/trialLessonReschedule";
import { MyError } from "../../../../shared/lib/error/MyError";
import { AuthError } from "../../../../shared/lib/error/AuthError";
import { useMediaQuery } from "../../../../shared/lib/hooks/useMediaQuery";
import { IGetCalendarFilterOptionsResponse } from "../../../../entities/calendar";
import { LoaderSpinner } from "../../../../shared/ui/spinner";
import { getTrialCalendarSlotsByIndex, getPositionedTrialLessons, 
    getTrialCalendarLessonLayout } from "../../lib/lessonLayout";
import { useTrialCalendarDaySelection } from "../../lib/useTrialCalendarDaySelection";
import { formatTrialTime, getTrialDaySlots, getTrialLessonTimeRange, getTrialSlotDateEnd, 
    getTrialVisibleRange } from "../../lib/date";
import { TrialCalendarDaySlots } from "../slots/TrialCalendarDaySlots";
import { TrialAvailableSlots } from "../slots/TrialAvailableSlots";
import classes from '../trialCalendarDay.module.scss'
import { TrialLessonActions } from "../../../../features/trialLessonActions";

interface IProps {
    date: Date;
    filter: ITrialCalendarFilter;
    creationFilter?: ITrialCalendarFilter;
    filterOptions: IGetCalendarFilterOptionsResponse | null;
    creationMode: boolean;
    availableSlotsOnly?: boolean;
    trialLessonId?: string;
    onSelectTrialLesson?: (lessonId: string) => void;
    onCloseTrialLesson?: () => void;
    onContentOpen?: () => void;
    onContentChange?: (isOpen: boolean) => void;
}

const getTrialLessonStatusClass = (status: TTrialLessonStatus): string => {
    if(status === 'TRIAL_LESSON_STATUS_COMPLETED') return classes.completedLesson
    if(status === 'TRIAL_LESSON_STATUS_STUDENT_CREATED') return classes.studentCreatedLesson
    if(status === 'TRIAL_LESSON_STATUS_LOST') return classes.lostLesson
    if(status === 'TRIAL_LESSON_STATUS_ORANGE_PENDING') return classes.orangeLesson

    return classes.assignedLesson
}

export const TrialCalendarDay: FC<IProps> = ({
    date, filter, creationFilter, filterOptions, creationMode, availableSlotsOnly, trialLessonId,
    onSelectTrialLesson, onCloseTrialLesson, onContentOpen, onContentChange,
}) => {

    const [isLoading, setIsLoading] = useState<boolean>(true)
    const [calendarDay, setCalendarDay] = useState<IGetTrialDayCalendarResponse | null>(null)
    const [selectedTrialLesson, setSelectedTrialLesson] = useState<ITrialLesson | null>(null)
    const [trialLessonDetails, setTrialLessonDetails] = useState<ITrialLesson | null>(null)
    const [isTrialLessonDetailsLoading, setIsTrialLessonDetailsLoading] = useState<boolean>(false)
    const [trialLessonDetailsError, setTrialLessonDetailsError] = useState<string>('')
    const [selectedPeriod, setSelectedPeriod] = useState<{startsAt: Date; durationSlots: number} | null>(null)
    const [reloadKey, setReloadKey] = useState<number>(0)
    const [trialLessonAction, setTrialLessonAction] = useState<TTrialLessonAction | null>(null)

    const {setIsAuth} = useMyActions()
    const {setGlobalMessage} = useGlobalMessageActions()
    const navigate = useNavigate()
    const location = useLocation()
    const isMobile = useMediaQuery('(max-width: 600px)')
    const isCompactTime = useMediaQuery('(max-width: 400px)')
    const displayTime = useCallback((value: Date): string => {
        const formattedTime = formatTrialTime(value)

        return isCompactTime ? formattedTime.replace(/:00$/, '') : formattedTime
    }, [isCompactTime])

    const onBack = () => {
        navigate({pathname: CALENDAR_TRIAL_ROUTE.path, search: location.search})
    }

    const slots = useMemo(() => getTrialDaySlots(date), [date])
    const selectedTrialLessonId = trialLessonId ?? selectedTrialLesson?.id
    const hasOpenContent = Boolean(selectedPeriod || selectedTrialLessonId)

    useEffect(() => {
        onContentChange?.(hasOpenContent)
    }, [hasOpenContent, onContentChange])

    useEffect(() => {
        return () => onContentChange?.(false)
    }, [onContentChange])

    useEffect(() => {
        let isActual = true

        const getData = async () => {
            try{
                setIsLoading(true)
                setCalendarDay(null)
                setSelectedTrialLesson(null)
                setSelectedPeriod(null)
                setTrialLessonAction(null)

                const response = await trialLessonService.getTrialDayCalendar({
                    date: {
                        year: date.getFullYear(),
                        month: date.getMonth() + 1,
                        day: date.getDate(),
                    },
                    filter,
                    creationFilter,
                })

                if(isActual) setCalendarDay(response)
            }
            catch(error){
                if(!isActual) return

                console.log(error)
                if(error instanceof AuthError){
                    setIsAuth(false)
                    setGlobalMessage({message: error.message, type: 'error'})
                }
                else if(error instanceof MyError){
                    setGlobalMessage({message: error.message, type: 'error'})
                }
                else{
                    setGlobalMessage({message: 'Ошибка при получении календаря пробных на день', type: 'error'})
                }
            }
            finally{
                if(isActual) setIsLoading(false)
            }
        }

        getData()

        return () => {
            isActual = false
        }
    }, [date, filter, creationFilter, reloadKey, setGlobalMessage, setIsAuth])

    useEffect(() => {
        if(!selectedTrialLessonId){
            setTrialLessonDetails(null)
            setTrialLessonDetailsError('')
            setIsTrialLessonDetailsLoading(false)
            return
        }

        let isActual = true

        const getTrialLesson = async () => {
            try{
                setIsTrialLessonDetailsLoading(true)
                setTrialLessonDetails(null)
                setTrialLessonDetailsError('')

                const response = await trialLessonService.getTrialLesson(selectedTrialLessonId)

                if(isActual) setTrialLessonDetails(response.trialLesson)
            }
            catch(error){
                if(!isActual) return

                console.log(error)
                const message = error instanceof MyError
                    ? error.message
                    : 'Ошибка при получении пробного занятия'

                setTrialLessonDetailsError(message)

                if(error instanceof AuthError){
                    setIsAuth(false)
                    setGlobalMessage({message: error.message, type: 'error'})
                }
                else{
                    setGlobalMessage({message, type: 'error'})
                }
            }
            finally{
                if(isActual) setIsTrialLessonDetailsLoading(false)
            }
        }

        getTrialLesson()

        return () => {
            isActual = false
        }
    }, [selectedTrialLessonId, setGlobalMessage, setIsAuth])

    const title = date.toLocaleDateString('ru-RU', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
    })

    const calendarSlotsByIndex = useMemo(() => {
        return getTrialCalendarSlotsByIndex(calendarDay?.slots ?? [], date)
    }, [calendarDay, date])

    const positionedLessons = useMemo(() => {
        return getPositionedTrialLessons(calendarDay?.slots ?? [], date)
    }, [calendarDay, date])

    const {visibleLessons, groupedLessons} = useMemo(() => {
        return getTrialCalendarLessonLayout(
            positionedLessons,
            isMobile,
        )
    }, [isMobile, positionedLessons])

    const {slotsRef, activeSelection, onPointerDown,
        onPointerMove, onPointerUp, cancelSelection,
    } = useTrialCalendarDaySelection({
		slots,
		calendarSlotsByIndex,
		enabled: creationMode,
		requiredDurationSlots: creationFilter?.requiredDurationSlots,
		onSelectPeriod: setSelectedPeriod,
	})

    const getVisibleTrialLessonTimeRange = useCallback((trialLesson: ITrialLesson): string => {
        const visibleRange = getTrialVisibleRange(date, trialLesson.startsAt, trialLesson.durationSlots)

        if(!visibleRange) return getTrialLessonTimeRange(trialLesson, displayTime)

        return `${displayTime(visibleRange.visibleStartsAt)}–${displayTime(visibleRange.visibleEndsAt)}`
    }, [date, displayTime])

    const selectTrialLesson = useCallback((trialLesson: ITrialLesson) => {
        if(onSelectTrialLesson){
            onSelectTrialLesson(trialLesson.id)
            return
        }

        setSelectedTrialLesson(trialLesson)
    }, [onSelectTrialLesson])

    const closeTrialLesson = () => {
        setSelectedTrialLesson(null)
        onCloseTrialLesson?.()
    }

    if(selectedPeriod){
        return (
            <TrialLessonCreate
                startsAt={selectedPeriod.startsAt}
                durationSlots={selectedPeriod.durationSlots}
                filter={creationFilter ?? filter}
                filterOptions={filterOptions}
                formatTime={formatTrialTime}
                onMount={onContentOpen}
                onCancel={() => setSelectedPeriod(null)}
                onCreated={() => {
                    setSelectedPeriod(null)
                    setReloadKey(value => value + 1)
                }}
            />
        )
    }
    if(selectedTrialLessonId){
        if(isTrialLessonDetailsLoading || !trialLessonDetails){
            return (
                <section className={classes.container}>
                    <section className={classes.header}>
                        <button type="button" className={classes.backButton} onClick={closeTrialLesson}>
                            <span aria-hidden="true">‹</span>
                            К пробным занятиям
                        </button>
                    </section>
                    {isTrialLessonDetailsLoading ? (
                        <section className={classes.loader}>
                            <LoaderSpinner />
                        </section>
                    ) : (
                        <section className={classes.detailsError}>
                            {trialLessonDetailsError || 'Не удалось загрузить пробное занятие'}
                        </section>
                    )}
                </section>
            )
        }

        const startsAt = new Date(trialLessonDetails.startsAt)
        const endsAt = getTrialSlotDateEnd(startsAt, trialLessonDetails.durationSlots)

        return (
            <>
                <TrialLessonCard
                    trialLesson={trialLessonDetails}
                    startsAt={startsAt}
                    endsAt={endsAt}
                    formatTime={formatTrialTime}
                    onBack={closeTrialLesson}
                    onMount={onContentOpen}
                    onAction={setTrialLessonAction}
                />
                {trialLessonAction && (
                    trialLessonAction === 'TRIAL_LESSON_ACTION_CREATE_STUDENT' ? (
                        <TrialLessonCreateStudent
                            open
                            setOpen={open => {
                                if(!open) setTrialLessonAction(null)
                            }}
                            trialLesson={trialLessonDetails}
                            onSuccess={() => {
                                setTrialLessonAction(null)
                                closeTrialLesson()
                                setReloadKey(value => value + 1)
                            }}
                        />
                    ) : trialLessonAction === 'TRIAL_LESSON_ACTION_EDIT' ? (
                        <TrialLessonEdit
                            open
                            setOpen={open => {
                                if(!open) setTrialLessonAction(null)
                            }}
                            trialLesson={trialLessonDetails}
                            onSuccess={() => {
                                setTrialLessonAction(null)
                                closeTrialLesson()
                                setReloadKey(value => value + 1)
                            }}
                        />
                    ) : trialLessonAction === 'TRIAL_LESSON_ACTION_RESCHEDULE' ? (
                        <TrialLessonReschedule
                            open
                            setOpen={open => {
                                if(!open) setTrialLessonAction(null)
                            }}
                            trialLesson={trialLessonDetails}
                            onSuccess={() => {
                                setTrialLessonAction(null)
                                closeTrialLesson()
                                setReloadKey(value => value + 1)
                            }}
                        />
                    ) : (
                        <TrialLessonActions
                            open
                            setOpen={open => {
                                if(!open) setTrialLessonAction(null)
                            }}
                            trialLesson={trialLessonDetails}
                            action={trialLessonAction}
                            onSuccess={() => {
                                setTrialLessonAction(null)
                                closeTrialLesson()
                                setReloadKey(value => value + 1)
                            }}
                        />
                    )
                )}
            </>
        )
    }

    return (
        <section className={classes.container}>
            <section className={classes.header}>
                <button type="button" className={classes.backButton} onClick={onBack}>
                    <span aria-hidden="true">‹</span>
                    Пробные за месяц
                </button>
                <h2 className={classes.title}>{title}</h2>
            </section>

            {isLoading && (
                <section className={classes.loader}>
                    <LoaderSpinner />
                </section>
            )}

            {!isLoading && calendarDay && (
                creationMode && availableSlotsOnly ? <TrialAvailableSlots
                    slots={calendarDay.slots ?? []}
                    durationSlots={creationFilter?.requiredDurationSlots ?? filter.requiredDurationSlots}
                    onSelect={setSelectedPeriod}
                /> :
                <TrialCalendarDaySlots
                    slotsRef={slotsRef}
                    slots={slots}
                    calendarSlotsByIndex={calendarSlotsByIndex}
					creationMode={creationMode}
                    activeSelection={activeSelection}
                    visibleLessons={visibleLessons}
                    groupedLessons={groupedLessons}
                    compactTime={isCompactTime}
                    formatTime={displayTime}
                    getTrialLessonTimeRange={getVisibleTrialLessonTimeRange}
                    getTrialLessonStatusClass={getTrialLessonStatusClass}
                    onPointerDown={onPointerDown}
                    onPointerMove={onPointerMove}
                    onPointerUp={onPointerUp}
                    onPointerCancel={cancelSelection}
                    onSelectTrialLesson={selectTrialLesson}
                />
            )}
        </section>
    )
}
